import React, { useRef, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import logo from "../../../assets/images/logo-rewind.png";
import FormGroup from "../../../components/common/FormGroup";
import {
    SingleAudioUploaderField,
    TextField,
    MultiSelectDropdownField,
    DropdownField,
} from "../../../components/common/FormFields";
import Button from "../../../components/ui/Button";
import { Tooltip } from "@mui/material";
import { SoundLibraryAI } from "../../../types/types";
import { createSoundLibraryAI, updateSoundLibraryAI } from "../../../api/soundLibrary";
import { useNotification } from "../../../context/NotificationContext";
import { buildSoundLocalizations, findSoundLocalization } from "../../../utils/soundLibraryLocalizations";
import adminStyles from "./SoundLibraryAdmin.module.css";
import studio from "../../../components/modals/StudioModal.module.css";

interface CreateSoundLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: any) => void;
    editItem?: SoundLibraryAI | null;
    typeOptions: { label: string; value: string }[];
    categoryOptions: { label: string; value: string }[];
}

interface FormValues {
    url: string;
    name_en: string;
    description_en: string;
    name_fr: string;
    description_fr: string;
    name_pt: string;
    description_pt: string;
    name_es: string;
    description_es: string;
    typeId: string;
    categoryIds: string[];
}

const CreateSoundLibraryModal: React.FC<CreateSoundLibraryModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    editItem,
    typeOptions,
    categoryOptions,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalRef = useRef<HTMLDivElement>(null);
    const { showNotification } = useNotification();

    const localizedEN = findSoundLocalization(editItem?.soundLibraryAILocalized, "EN");
    const localizedFR = findSoundLocalization(editItem?.soundLibraryAILocalized, "FR");
    const localizedPT = findSoundLocalization(editItem?.soundLibraryAILocalized, "PT");
    const localizedES = findSoundLocalization(editItem?.soundLibraryAILocalized, "ES");

    // Pull out the “typeId” from one of the typeLocalizations (they all share the same soundLibraryAITypeId):
    const editTypeId = editItem?.typeLocalizations?.[0]?.soundLibraryAITypeId || "";

    // Pull out all category IDs from editItem.categories:
    const editCategoryIds = editItem?.categories?.map((c) => c.id) || [];

    const initialValues: FormValues = {
        url: editItem?.url || "",
        name_en: localizedEN.name || "",
        description_en: localizedEN.soundDescription || "",
        name_fr: localizedFR.name || "",
        description_fr: localizedFR.soundDescription || "",
        name_pt: localizedPT.name || "",
        description_pt: localizedPT.soundDescription || "",
        name_es: localizedES.name || "",
        description_es: localizedES.soundDescription || "",
        typeId: editTypeId,
        categoryIds: editCategoryIds,
    };

    const validationSchema = Yup.object().shape({
        url: Yup.string().url(t("validUrlRequired")).required(t("urlRequired")),
        name_en: Yup.string().required(t("nameRequired")),
        description_en: Yup.string(),
        name_fr: Yup.string().required(t("nameRequired")),
        description_fr: Yup.string(),
        typeId: Yup.string(),
        categoryIds: Yup.array(),
    });

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    const sortedTypeOptions = useMemo(() => {
        return [...typeOptions].sort((a, b) => a.label.localeCompare(b.label));
    }, [typeOptions]);

    const sortedCategoryOptions = useMemo(() => {
        return [...categoryOptions].sort((a, b) => a.label.localeCompare(b.label));
    }, [categoryOptions]);

    if (!isOpen) return null;

    const modal = (
        <div className={studio.adminOverlay}>
            <div className={adminStyles.soundModal} ref={modalRef}>
                <div className={studio.adminHeader}>
                    <img src={logo} alt="Logo" className={studio.adminLogo} />
                    <h1 className={studio.adminTitle}>{editItem ? t("editSound") : t("createSound")}</h1>
                    <button className={studio.adminClose} onClick={onClose}>
                        ×
                    </button>
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnMount
                    validateOnChange
                    enableReinitialize
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            const payload = {
                                url: values.url,
                                name: values.name_en,
                                soundDescription: values.description_en,
                                soundLibraryAITypeId: values.typeId,
                                soundLibraryAICategoryIds: values.categoryIds,
                                localizations: buildSoundLocalizations(values),
                            };

                            const token = localStorage.getItem("token") || "";
                            if (editItem && editItem.id) {
                                await updateSoundLibraryAI({ token, soundId: editItem.id, data: payload });
                            } else {
                                await createSoundLibraryAI({ token, data: payload });
                            }

                            onSubmit(payload);
                            onClose();
                        } catch (error) {
                            console.error("Error saving item:", error);
                            showNotification({
                                message: editItem ? t("errorUpdatingSound") : t("errorCreatingSound"),
                                type: "error",
                            });
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ submitForm, errors, isSubmitting }) => (
                        <Form className={adminStyles.soundForm}>
                            <div className="form-content">
                                <FormGroup title={t("uploadFile")} size="small" required>
                                    <Field name="url" component={SingleAudioUploaderField} />
                                    <ErrorMessage name="url" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("nameInEnglish")} size="small" required>
                                    <Field name="name_en" component={TextField} />
                                    <ErrorMessage name="name_en" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("descInEnglish")} size="small">
                                    <Field name="description_en" component={TextField} as="textarea" rows={3} />
                                    <ErrorMessage name="description_en" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("nameInFrench")} size="small" required>
                                    <Field name="name_fr" component={TextField} />
                                    <ErrorMessage name="name_fr" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("descInFrench")} size="small">
                                    <Field name="description_fr" component={TextField} as="textarea" rows={3} />
                                    <ErrorMessage name="description_fr" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("nameInPortuguese")} size="small">
                                    <Field name="name_pt" component={TextField} />
                                    <ErrorMessage name="name_pt" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("descInPortuguese")} size="small">
                                    <Field name="description_pt" component={TextField} as="textarea" rows={3} />
                                    <ErrorMessage name="description_pt" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("nameInSpanish")} size="small">
                                    <Field name="name_es" component={TextField} />
                                    <ErrorMessage name="name_es" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("descInSpanish")} size="small">
                                    <Field name="description_es" component={TextField} as="textarea" rows={3} />
                                    <ErrorMessage name="description_es" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("type")} size="small">
                                    <Field
                                        name="typeId"
                                        component={DropdownField}
                                        isWithoutMargin={true}
                                        options={sortedTypeOptions}
                                    />
                                    <ErrorMessage name="typeId" component="div" className="error-message" />
                                </FormGroup>

                                <FormGroup title={t("category")} size="small">
                                    <Field
                                        name="categoryIds"
                                        component={MultiSelectDropdownField}
                                        isWithoutMargin={true}
                                        options={sortedCategoryOptions}
                                        placeholder={t("selectCategories")}
                                        menuPlacement="top"
                                        maxHeight={300}
                                    />
                                    <ErrorMessage name="categoryIds" component="div" className="error-message" />
                                </FormGroup>
                            </div>

                            <div className="form-actions">
                                <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                                <Tooltip
                                    title={
                                        Object.keys(errors).length > 0
                                            ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                            : ""
                                    }
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="primary"
                                            label={editItem ? t("update") : t("create")}
                                            onClick={submitForm}
                                            disabled={isSubmitting || Object.keys(errors).length > 0}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default CreateSoundLibraryModal;
